import { IconBox, Input, Type as T } from "@infobis/ui-styles-utils";
import React, { MouseEvent, useMemo, useState } from "react";
import { useTypedSelector } from "../../../hooks/useTypedSelector";
import cn from "classnames";
import { escapeRegExp } from "lodash";
import { Organization, TNode } from "../../../pages/auth/authPage/AuthPage";
import st from "./styles.module.scss";
import { useActions } from "@hooks/useActions";
import { useEffectOnce } from "usehooks-ts";
import LoadingBox from "@elements/loading/LoadingBox";
import { AsClientNode } from "@api/auth/OrganizationService";
import { PortalComputedProps } from "@infobis/ui-styles-utils/components/Portal/Portal";
import colors from "@infobis/ui-styles-utils/colors";

type DepartmentsTreeProps<T> = PortalComputedProps<T> & {
    onChoose?: (node: (TNode<Organization> | undefined)) => void,
    onClose: () => void
}
export default React.forwardRef<HTMLDivElement, DepartmentsTreeProps<HTMLDivElement>>((props, ref) => {
    const { tree, isApiLoading } = useTypedSelector(state => state.organizations)
    const { fetchOrganizationsTree } = useActions()

    useEffectOnce(() => {
        if (!tree) fetchOrganizationsTree()
    })

    const [expanded, setExpanded] = useState<string[]>([]);
    const [searchInput, setSearchInput] = useState<string>("");
    const [selectedNode, setSelectedNode] = useState<TNode<Organization> | undefined>();

    const mutate = <T, >(el: T) => (state: T[]) => {
        const idx = state.indexOf(el)
        idx !== -1 ? state.splice(idx, 1) : state.push(el)
        return [...state]
    }
    const onSearchInputChange = (e: string) =>
        setSearchInput(e)

    const onNodeChoose = () =>
        props.onChoose && props.onChoose(selectedNode)

    const renderTNode = (node: TNode<Organization>, idx: number) => {
        const isExpanded = searchInput.length > 0 || expanded.indexOf(node.id) !== -1

        const onExpandClick = (e: MouseEvent) => {
            e.stopPropagation()
            setExpanded(mutate(node.id))
        }
        const onNodeClick = (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            setSelectedNode(node)
        }

        const renderLi = () => <li className={cn(
            st['departments__li'],
            st['departments__li--' + idx]
        )} key={node.id}>
            <div
                style={{ '--tree-level': idx } as React.CSSProperties}
                onClick={onNodeClick}
                className={cn(
                    st['departments__li-wrapper'],
                    selectedNode?.id === node.id && st['departments__li-wrapper--active'],
                )}
            ><div className={st['departments__li-name']}>{node.name}</div>
                {!node.isLeaf && (
                    <div className={st['departments__li-expanded-tool']} onClick={onExpandClick}>
                        <IconBox
                            icon={isExpanded ? T.IconName.ChevronDown : T.IconName.ChevronRight}
                            size={T.Size.SMALL}
                            sizePadding={T.Size.AUTO}
                            color={colors.CONTROL_DEFAULT_ICON_ICON}
                        />
                    </div>
                )}
            </div>
            {isExpanded && <ul className={st.departments__ul}>{node.data.map(n => renderTNode(n, idx + 1))}</ul>}
        </li>
        return searchInput ? node.isValid && renderLi() : renderLi()
    }
    const traversedTree: TNode<Organization>[] = useMemo(() => {
        const re = new RegExp(escapeRegExp(searchInput), "i")

        const checkNode = (node: TNode<Organization>, children: TNode<any>[]) => {
            const isValid = searchInput ? re.test(node.name) || children.some(x => x.isValid) : true
            return { ...node, isValid }
        }

        return tree ? tree.data.map(node => {
            const department2Node: (department: AsClientNode) => TNode<Organization>[] = (department: AsClientNode) => {
                const data = department.data.flatMap(department2Node)
                return [checkNode({
                    id: String(department.glid),
                    name: department.name,
                    isLeaf: department.leaf,
                    underlying: { ...department, companyId: String(node.clientId), kind: "department" },
                    data
                }, data)]
            }
            const data = node.data.flatMap(department2Node)
            return checkNode({
                id: String(node.glid),
                name: node.name,
                underlying: { ...node, kind: "company" },
                isLeaf: node.leaf,
                data
            }, data)
        }) : []
    }, [tree, searchInput])

    return <div style={props.style}
                ref={ref}
                className={st.departments}
    >
        {isApiLoading ? <LoadingBox/> : <>
            <section className={st.departments__header}>
                <h4 className={st['departments__header-title']}>Список департаментов</h4>
                <div className={st['departments__header-x']}>
                    <IconBox
                        icon={T.IconName.Cancel}
                        size={T.Size.SMALL}
                        onClick={props.onClose}
                    />
                </div>
            </section>
            <section className={st.departments__body}>
                <div className={st['departments__body-search']}>
                    <Input.Text
                        inputClassName={st.search_box}
                        iconAfter={() =>
                            searchInput.length === 0 ? <IconBox
                                icon={T.IconName.Search}
                                color={colors.CONTROL_DEFAULT_ICON_ICON}
                                size={T.Size.LARGE}
                            /> : <IconBox
                                style={{ cursor: 'pointer' }}
                                icon={T.IconName.Cancel}
                                onClick={() => setSearchInput("")}
                                color={colors.CONTROL_DEFAULT_ICON_ICON}
                                size={T.Size.SMALL}
                            />
                        }
                        value={searchInput}
                        onChange={onSearchInputChange}
                        width={T.Width.AUTO}
                    />
                </div>
                <div className={st['departments__body-tree']}>
                    <ul className={st.departments__ul}>{traversedTree.map(node => renderTNode(node, 0))}</ul>
                </div>
            </section>
            <section className={st.departments__footer}>
                <button
                    onClick={onNodeChoose}
                    className={st.departments__button}>Выбрать
                </button>
            </section>
        </>}
    </div>
})
